/**
 * SecureFileSettings — Settings → Secure Information File (s89).
 *
 * The planner's management surface for the SDE (Cat A) Secure Information File.
 * Surfaces status (set up? where stored? passphrase-protected?) and the file actions:
 *   - Open secure file         → Phase 3 open flow (useSecureFileOpen)
 *   - Download a fresh copy     → Phase 2 save flow WITHOUT a handle (picker/download)
 *   - Update storage location   → inline-editable; persists to the non-sensitive
 *                                 `secure_file_location` About You field
 *   - Change passphrase         → re-encrypts the current in-memory values, in place
 *                                 (existing handle) or as a download
 *   - Start a new file          → reuses SecureFileOnboardingStep (fresh passphrase/location)
 *
 * 🔒 Data-loss guard: the in-memory store is EMPTY unless the planner opened their file or
 * typed Cat A values this session. "Download a fresh copy" and "Change passphrase" therefore
 * require session.hasValues — otherwise they'd write an empty file over a real backup. "Open"
 * is always safe; "Start a new file" is a deliberate reset and doesn't guard.
 *
 * Mounted inside SettingsPage, which already lives within SensitiveDataSessionProvider +
 * SecureFileOpenProvider (ClientDashboard), so the session/open hooks are available here.
 */
import { useState } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ShieldCheck, ShieldOff, Lock, FolderOpen, Download, KeyRound, FilePlus2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useSensitiveDataSession } from "@/lib/sensitive-data-session-provider";
import { useSecureFileOpen } from "@/components/SecureFileOpenProvider";
import { saveSecureFile, NO_PASSPHRASE_KEY } from "@/lib/secure-file-save";
import { secureFileName } from "@/lib/secure-file-name";
import { SecureFileOnboardingStep } from "@/components/SecureFileOnboardingStep";
import { useAuth } from "@/contexts/AuthContext";
import { CATEGORY_SLUGS } from "@shared/constants/categories";
import type { Client } from "@shared/schema";
import { SAGE } from "@shared/brand";

function formatDate(d: Date | string | null | undefined): string | null {
  if (!d) return null;
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function SecureFileSettings({ clientData }: { clientData: Client }) {
  const { toast } = useToast();
  const session = useSensitiveDataSession();
  const { user } = useAuth();
  const { openFile } = useSecureFileOpen();
  const [, setLocation] = useLocation();

  const setupComplete = !!clientData.secureFileSetupAt;

  // s111: descriptive, stable filename — planner name + creation date (secure_file_setup_at
  // if the file already exists, else today). "Start a new file" reuses secure_file_setup_at
  // by design (accepted edge case: a replacement file keeps the original creation date).
  const fileName = secureFileName(user?.fullName, clientData.secureFileSetupAt ?? new Date());

  // Current recorded storage location (non-sensitive) — surfaced via the WIE aggregate,
  // same source SecureFileStatusCard reads.
  const { data: wie = [] } = useQuery<Array<{ fieldKey?: string | null; location?: string }>>({
    queryKey: ["/api/clients", clientData.id, CATEGORY_SLUGS.WHERE_IS_EVERYTHING],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientData.id}/where-is-everything`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!clientData.id,
  });
  const recordedLocation = wie.find((r) => r.fieldKey === "secure_file_location")?.location?.trim() ?? "";

  // ── Local UI state ────────────────────────────────────────────────────────
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [newFileWarnOpen, setNewFileWarnOpen] = useState(false);
  const [locationDraft, setLocationDraft] = useState<string | null>(null); // null → not editing; mirrors recorded
  const [showPassphraseForm, setShowPassphraseForm] = useState(false);
  const [changePassEnabled, setChangePassEnabled] = useState(false); // seeded from current status on open
  const [newPassphrase, setNewPassphrase] = useState("");
  const [busyAction, setBusyAction] = useState<"download" | "passphrase" | null>(null);
  // Save performed within this Settings session (more recent than secureFileSetupAt).
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  const locationValue = locationDraft ?? recordedLocation;

  // Passphrase status is only knowable if the file was opened/created this session.
  const pp = session.passphrase;
  const passphraseStatus: "protected" | "none" | "unknown" =
    pp == null ? "unknown" : pp === NO_PASSPHRASE_KEY ? "none" : "protected";

  // ── Mutations ─────────────────────────────────────────────────────────────
  // D-170.6: converted from a raw apiRequest. Class B-idempotent — records secure-file setup
  // completion (and optionally the location string); a duplicate lands on the same state.
  const setupCompleteMutation = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      await apiRequest("POST", `/api/clients/${clientData.id}/secure-file-setup-complete`, body),
  });

  const saveLocationMutation = useMutation({
    mutationFn: async (location: string) => {
      const res = await apiRequest("POST", `/api/clients/${clientData.id}/secure-file-setup-complete`, { location });
      if (!res.ok) throw new Error((await res.json()).message || "Failed to save location");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData.id, CATEGORY_SLUGS.WHERE_IS_EVERYTHING] });
      setLocationDraft(null);
      toast({ title: "Storage location saved" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const handleLocationBlur = () => {
    if (locationDraft == null) return; // not edited
    const trimmed = locationDraft.trim();
    if (trimmed === recordedLocation) { setLocationDraft(null); return; } // unchanged
    saveLocationMutation.mutate(trimmed);
  };

  // Reuses SensitiveFieldInput.handleSetupComplete's contract: complete the session, then
  // record setup + the non-sensitive location server-side. Used by both "Set up now" and
  // "Start a new file" (both flow through SecureFileOnboardingStep, which builds the file).
  const handleSetupComplete = async (handle: FileSystemFileHandle | null, passphrase: string, location?: string) => {
    session.completeSetup(handle, passphrase);
    setOnboardingOpen(false);
    setLastSavedAt(new Date());
    try {
      // D-170.6: class B — records setup completion + a location string, last-wins.
      await setupCompleteMutation.mutateAsync(location ? { location } : {});
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData.id, CATEGORY_SLUGS.WHERE_IS_EVERYTHING] });
    } catch {
      // Non-fatal: the session is set up; the server record re-syncs on the next save.
    }
  };

  // Download a FRESH copy: no handle → always a picker (Chrome/Edge) or browser download,
  // never a silent overwrite of the primary file. Guarded on hasValues (see header note).
  const handleDownloadFresh = async () => {
    if (!session.hasValues) {
      toast({
        title: "Open your file first",
        description: "Open your secure file so it holds your current information, then download a copy.",
      });
      return;
    }
    setBusyAction("download");
    try {
      const result = await saveSecureFile({ values: session.getAllValues(), passphrase: session.passphrase, fileName });
      session.markSaved(Object.keys(session.getAllValues()));
      setLastSavedAt(new Date());
      toast({
        title: result.method === "download" ? "Fresh copy downloaded" : "Fresh copy saved",
        duration: 3000,
      });
    } catch (err: any) {
      if (err?.name !== "AbortError") {
        toast({ title: "Couldn't save a copy", description: "Please try again.", variant: "destructive" });
      }
    } finally {
      setBusyAction(null);
    }
  };

  // Change passphrase: re-encrypt the current in-memory values. In place when a handle exists
  // (Chrome/Edge), otherwise a download the planner uses to replace their file. Updates the
  // in-memory passphrase so subsequent autosaves use the new one. Guarded on hasValues.
  const handleChangePassphrase = async () => {
    if (!session.hasValues) {
      toast({
        title: "Open your file first",
        description: "Open your secure file before changing its passphrase.",
      });
      return;
    }
    setBusyAction("passphrase");
    try {
      const values = session.getAllValues();
      // Toggle off → empty next → removal (re-encrypt with NO_PASSPHRASE_KEY), same as the old
      // "leave blank to remove" path. Toggle on → the typed passphrase.
      const next = changePassEnabled ? newPassphrase.trim() : "";
      const result = await saveSecureFile({ values, passphrase: next || undefined, handle: session.fileHandle, fileName });
      session.completeSetup(result.handle ?? session.fileHandle, next || NO_PASSPHRASE_KEY);
      session.markSaved(Object.keys(values));
      setLastSavedAt(new Date());
      setShowPassphraseForm(false);
      setNewPassphrase("");
      toast({
        title: next ? "Passphrase updated" : "Passphrase removed",
        description: result.method === "download"
          ? "Replace your old file with the copy you just downloaded."
          : undefined,
        duration: 4000,
      });
    } catch (err: any) {
      if (err?.name !== "AbortError") {
        toast({ title: "Couldn't update the passphrase", description: "Please try again.", variant: "destructive" });
      }
    } finally {
      setBusyAction(null);
    }
  };

  // Open/close the change-passphrase form. On open, seed the checkbox from the CURRENT status:
  // protected → on (don't imply removal), none/unknown → off (the consistent default). On close,
  // always clear the typed value.
  const togglePassphraseForm = () => {
    setShowPassphraseForm((open) => {
      const next = !open;
      if (next) setChangePassEnabled(passphraseStatus === "protected");
      setNewPassphrase("");
      return next;
    });
  };

  const handleToggleChangePass = (checked: boolean) => {
    setChangePassEnabled(checked);
    if (!checked) setNewPassphrase(""); // no stale value when toggled back off
  };

  const savedDate = formatDate(lastSavedAt) ?? formatDate(clientData.secureFileSetupAt);

  // ── Not set up ────────────────────────────────────────────────────────────
  // s112 SF-4: creation lives on the Protect Your Access page (the single setup home, s111).
  // Settings is a MANAGEMENT surface — so the empty state POINTS to PYA instead of offering a
  // third inline "Set up now" create path (which had diverged from the consolidated flow).
  if (!setupComplete) {
    return (
      <div id="secure-file">
        <Card data-testid="secure-file-settings">
          <CardHeader>
            <CardTitle>Secure Information File</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-2.5 text-sm text-muted-foreground">
              <ShieldOff className="h-4 w-4 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="font-medium text-foreground">Your secure file hasn't been set up yet.</p>
                <p>
                  Sensitive information like passwords and government IDs is stored in a file on your
                  computer, never on our servers.
                </p>
                <p>
                  You set it up on the{" "}
                  <span className="font-medium text-foreground">Protect Your Access</span> page. Once
                  it exists, you can open, download, and manage it here.
                </p>
              </div>
            </div>
            <Button
              onClick={() => setLocation(`/category/${CATEGORY_SLUGS.PROTECT_YOUR_ACCESS}`)}
              data-testid="button-setup-goto-pya"
            >
              Go to Protect Your Access
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Set up ────────────────────────────────────────────────────────────────
  return (
    <div id="secure-file">
      <Card data-testid="secure-file-settings">
        <CardHeader>
          <CardTitle>Secure Information File</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Status block */}
          <div className="space-y-3" data-testid="secure-file-settings-status">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 shrink-0" style={{ color: SAGE[600] }} />
              <span className="text-sm font-medium" style={{ color: SAGE[600] }}>Set up</span>
            </div>

            {savedDate && (
              <div className="flex items-baseline justify-between gap-4">
                <p className="text-xs text-muted-foreground">Last saved</p>
                <p className="text-sm">{savedDate}</p>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="sf-location" className="text-xs text-muted-foreground">Storage location</Label>
              <Input
                id="sf-location"
                data-testid="secure-file-settings-location"
                value={locationValue}
                onChange={(e) => setLocationDraft(e.target.value)}
                onBlur={handleLocationBlur}
                placeholder="e.g. USB drive in my home safe"
                disabled={saveLocationMutation.isPending}
              />
            </div>

            <div className="flex items-baseline justify-between gap-4">
              <p className="text-xs text-muted-foreground">Passphrase</p>
              <p className="text-sm flex items-center gap-1.5">
                {passphraseStatus === "protected" && (
                  <><Lock className="h-3.5 w-3.5" style={{ color: SAGE[600] }} /> Protected</>
                )}
                {passphraseStatus === "none" && <span className="text-muted-foreground">No passphrase</span>}
                {passphraseStatus === "unknown" && (
                  <span className="text-muted-foreground italic">Open your file to view</span>
                )}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-1 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={openFile}
              className="mt-4"
              data-testid="button-open-secure-file"
            >
              <FolderOpen className="h-4 w-4 mr-1.5" />
              Open secure file
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadFresh}
              disabled={busyAction === "download"}
              className="mt-4"
              data-testid="button-download-fresh-copy"
            >
              <Download className="h-4 w-4 mr-1.5" />
              {busyAction === "download" ? "Saving…" : "Download a fresh copy"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={togglePassphraseForm}
              className="mt-4"
              data-testid="button-change-passphrase"
            >
              <KeyRound className="h-4 w-4 mr-1.5" />
              Change passphrase
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setNewFileWarnOpen(true)}
              className="mt-4 text-muted-foreground"
              data-testid="button-start-new-file"
            >
              <FilePlus2 className="h-4 w-4 mr-1.5" />
              Start a new file
            </Button>
          </div>

          {/* Change-passphrase inline form — same toggle pattern as the setup dialog. */}
          {showPassphraseForm && (
            <div className="space-y-3 p-4 border rounded-lg" data-testid="secure-file-passphrase-form">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="sf-change-add-pass"
                  checked={changePassEnabled}
                  onCheckedChange={(checked) => handleToggleChangePass(checked === true)}
                  data-testid="secure-file-change-add-passphrase"
                />
                <Label htmlFor="sf-change-add-pass" className="text-sm font-medium cursor-pointer">
                  Add a passphrase for extra security
                </Label>
              </div>

              {changePassEnabled ? (
                <div>
                  <Input
                    id="sf-new-pass"
                    aria-label="New passphrase"
                    type="text"
                    value={newPassphrase}
                    onChange={(e) => setNewPassphrase(e.target.value)}
                    placeholder="Several unrelated words work best"
                    autoComplete="new-password"
                    autoFocus
                    data-testid="secure-file-new-passphrase"
                  />
                  <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                    Your executor will need this passphrase to open your secure file.
                    Only add one if you're confident they'll have it when the time comes.
                  </p>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Without a passphrase, your executor can open the file directly — nothing extra to
                  find or remember.
                </p>
              )}

              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleChangePassphrase}
                  disabled={busyAction === "passphrase"}
                  data-testid="button-confirm-passphrase"
                >
                  {busyAction === "passphrase" ? "Saving…" : "Save"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setShowPassphraseForm(false); setNewPassphrase(""); }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Start-a-new-file warning */}
      <AlertDialog open={newFileWarnOpen} onOpenChange={setNewFileWarnOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Start a new secure file?</AlertDialogTitle>
            <AlertDialogDescription>
              This creates a new file from your current data and sets a fresh passphrase.
              Your old file becomes unreadable. Use this if you've lost your file or want to start over.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setNewFileWarnOpen(false); setOnboardingOpen(true); }}>
              Start a new file
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {onboardingOpen && (
        <SecureFileOnboardingStep
          open={true}
          onOpenChange={setOnboardingOpen}
          fieldName="your sensitive information"
          fileName={fileName}
          onComplete={handleSetupComplete}
          onSkip={() => setOnboardingOpen(false)}
        />
      )}
    </div>
  );
}
